// config/appSetup.js - Application Setup with Authorization

import { configureAxios } from './axiosConfig';
import { clearAuthData } from '../utils/authUtils';

/**
 * Initialize the application with all required configurations
 */
export const initializeApp = () => {
    console.log('🚀 Initializing application...');

    // Configure axios interceptors
    configureAxios();

    // Setup global error handlers
    setupGlobalErrorHandlers();

    // Setup notification system if available
    setupNotificationSystem();

    // Clean up any invalid auth data
    validateAuthData();



    console.log('✅ Application initialized successfully');
};

/**
 * Setup global error handlers for unhandled promises and errors
 */
const setupGlobalErrorHandlers = () => {
    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
        console.error('Unhandled promise rejection:', event.reason);

        // Check if it's an authorization error
        if (event.reason?.response?.status === 401) {
            console.log('Global auth error detected, clearing auth data');
            clearAuthData();
            window.location.href = '/sign-in';
        } else if (event.reason?.response?.status === 403) {
            console.log('Global authorization error detected');
            console.log('You do not have permission to perform this action.');
        }

        // Prevent the default handling
        event.preventDefault();
    });

    // Handle general JavaScript errors
    window.addEventListener('error', (event) => {
        console.error('Global error:', event.error);

        // You can add error reporting service here
        // reportErrorToService(event.error);
    });
};

/**
 * Setup global notification system using Material-UI
 */
const setupNotificationSystem = () => {
    // Material-UI notification system
    if (!window.showNotification) {
        window.showNotification = (message, type = 'info', duration = 5000) => {
            // Filter out certain types of notifications to reduce spam
            const shouldShowNotification = (message, type) => {
                // Don't show technical error messages
                if (message?.includes('timeout') ||
                    message?.includes('network') ||
                    message?.includes('connection') ||
                    message?.includes('Failed to fetch') ||
                    message?.includes('ECONNREFUSED') ||
                    message?.includes('ENOTFOUND')) {
                    console.log('Suppressing technical notification:', message);
                    return false;
                }

                // Don't show generic error messages
                if (message?.includes('An unexpected error occurred') ||
                    message?.includes('Something went wrong')) {
                    console.log('Suppressing generic notification:', message);
                    return false;
                }

                // Don't show server error messages
                if (message?.includes('Server error') ||
                    message?.includes('500') ||
                    message?.includes('502') ||
                    message?.includes('503') ||
                    message?.includes('504')) {
                    console.log('Suppressing server error notification:', message);
                    return false;
                }

                return true;
            };

            // Check if notification should be shown
            if (!shouldShowNotification(message, type)) {
                return;
            }

            // Create notification container if it doesn't exist
            let notificationContainer = document.getElementById('notification-container');
            if (!notificationContainer) {
                notificationContainer = document.createElement('div');
                notificationContainer.id = 'notification-container';
                notificationContainer.style.cssText = `
          position: fixed;
          top: 16px;
          right: 16px;
          z-index: 9999;
          display: flex;
          flex-direction: column;
          gap: 8px;
          max-width: 400px;
        `;
                document.body.appendChild(notificationContainer);
            }

            // Check if we already have too many notifications (max 3)
            const existingNotifications = notificationContainer.children.length;
            if (existingNotifications >= 3) {
                console.log('Too many notifications, removing oldest');
                // Remove the oldest notification
                if (notificationContainer.firstChild) {
                    notificationContainer.firstChild.remove();
                }
            }

            // Create notification element with Material-UI styling
            const notification = document.createElement('div');
            notification.style.cssText = `
        display: flex;
        align-items: center;
        padding: 12px 16px;
        border-radius: 4px;
        box-shadow: 0 3px 5px -1px rgba(0,0,0,0.2), 0 6px 10px 0 rgba(0,0,0,0.14), 0 1px 18px 0 rgba(0,0,0,0.12);
        margin-bottom: 8px;
        min-width: 300px;
        max-width: 400px;
        ${getNotificationStyles(type)}
        animation: slideIn 0.3s ease-out;
      `;

            notification.innerHTML = `
        <div style="display: flex; align-items: center; width: 100%;">
          <div style="margin-right: 12px; display: flex; align-items: center;">
            ${getNotificationIcon(type)}
          </div>
          <div style="flex: 1; margin-right: 8px;">
            <p style="margin: 0; font-size: 14px; font-weight: 500; line-height: 1.4;">${message}</p>
          </div>
          <button onclick="this.parentElement.parentElement.remove()" style="
            background: none;
            border: none;
            cursor: pointer;
            padding: 4px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            color: inherit;
            opacity: 0.7;
            transition: opacity 0.2s;
          " onmouseover="this.style.opacity='1'" onmouseout="this.style.opacity='0.7'">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>
      `;

            // Add CSS animation
            if (!document.getElementById('notification-styles')) {
                const style = document.createElement('style');
                style.id = 'notification-styles';
                style.textContent = `
          @keyframes slideIn {
            from {
              transform: translateX(100%);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }
          @keyframes slideOut {
            from {
              transform: translateX(0);
              opacity: 1;
            }
            to {
              transform: translateX(100%);
              opacity: 0;
            }
          }
        `;
                document.head.appendChild(style);
            }

            // Add to container
            notificationContainer.appendChild(notification);

            // Auto remove after duration (reduced from 5000 to 3000 for faster dismissal)
            setTimeout(() => {
                if (notification.parentNode) {
                    notification.style.animation = 'slideOut 0.3s ease-in';
                    setTimeout(() => {
                        if (notification.parentNode) {
                            notification.remove();
                        }
                    }, 300);
                }
            }, Math.min(duration, 3000)); // Cap at 3 seconds max
        };
    }
};

/**
 * Get notification styles based on type (Material-UI colors)
 */
const getNotificationStyles = (type) => {
    switch (type) {
        case 'error':
            return `
        background-color: #ffebee;
        color: #c62828;
        border-left: 4px solid #f44336;
      `;
        case 'warning':
            return `
        background-color: #fff3e0;
        color: #ef6c00;
        border-left: 4px solid #ff9800;
      `;
        case 'success':
            return `
        background-color: #e8f5e8;
        color: #2e7d32;
        border-left: 4px solid #4caf50;
      `;
        default:
            return `
        background-color: #e3f2fd;
        color: #1565c0;
        border-left: 4px solid #2196f3;
      `;
    }
};

/**
 * Get notification icon based on type (Material-UI icons)
 */
const getNotificationIcon = (type) => {
    switch (type) {
        case 'error':
            return '<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>';
        case 'warning':
            return '<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/></svg>';
        case 'success':
            return '<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>';
        default:
            return '<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/></svg>';
    }
};

/**
 * Validate authentication data in localStorage
 */
const validateAuthData = () => {
    try {
        const authToken = localStorage.getItem('authToken');
        const authUser = localStorage.getItem('authUser');

        if (authToken && authUser) {
            // Try to parse user data
            const user = JSON.parse(authUser);

            // Basic validation
            if (!user.userId || !user.email) {
                console.log('Invalid auth data detected, clearing...');
                clearAuthData();
                return;
            }

            // Check if token is expired (basic check)
            try {
                const tokenParts = authToken.split('.');
                if (tokenParts.length === 3) {
                    const payload = JSON.parse(atob(tokenParts[1]));
                    const now = Math.floor(Date.now() / 1000);

                    if (payload.exp && payload.exp < now) {
                        console.log('Expired token detected, clearing auth data');
                        clearAuthData();
                        return;
                    }
                }
            } catch (tokenError) {
                console.error('Error validating token:', tokenError);
                clearAuthData();
                return;
            }

            console.log('✅ Auth data validation passed');
        }
    } catch (error) {
        console.error('Error validating auth data:', error);
        clearAuthData();
    }
};

/**
 * Setup development-only features
 */
const setupDevelopmentFeatures = () => {
    if (process.env.NODE_ENV === 'development') {
        // Add development-only global helpers
        window.debugAuth = () => {
            console.log('Auth Token:', localStorage.getItem('authToken'));
            console.log('Auth User:', JSON.parse(localStorage.getItem('authUser') || 'null'));
            console.log('Card State:', JSON.parse(localStorage.getItem('cardState') || '{}'));
        };

        window.clearAuth = () => {
            clearAuthData();
            console.log('Auth data cleared');
        };

        console.log('🔧 Development features enabled');

    }
};

/**
 * Check if user has required permissions for the current route
 */
export const checkRoutePermissions = (pathname, userRole = null) => {
    // Define protected routes and their required permissions
    const protectedRoutes = {
        '/admin': ['admin'],
        '/dashboard': ['user', 'admin'],
        '/cards': ['user', 'admin'],
        '/settings': ['user', 'admin']
    };

    // Check if route requires authentication
    const requiredRoles = protectedRoutes[pathname];
    if (!requiredRoles) {
        return true; // Public route
    }

    // Check if user has required role
    if (!userRole || !requiredRoles.includes(userRole)) {
        return false;
    }

    return true;
};

/**
 * Initialize app with development and features
 */
export const initializeAppWithDev = () => {
    initializeApp();
    setupDevelopmentFeatures();
};

// Export default initialization
export default initializeApp; 